import type { RoomParticipant } from '../useRoomControl';

interface Props {
  mutedAll: boolean;
  onToggleMuteAll: () => void;
  recording: boolean;
  elapsed: number;
  onToggleRecord: () => void;
  handsRaised: RoomParticipant[];
  onGrant: (id: string) => void;
  onGrantAll: () => void;
  onOpenVote: () => void;
  onOpenYoutube: () => void;
  youtubeActive: boolean;
  onStopYoutube: () => void;
  /** Nom du présentateur désigné (partage d'écran à distance). */
  presenterName?: string | null;
  /** Retirer le rôle de présentateur en cours. */
  onStopPresenter?: () => void;
}

function fmt(sec: number): string {
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/** Barre d'outils réservée à l'hôte : modération, sondage, vidéo, enregistrement. */
export default function HostControls({
  mutedAll, onToggleMuteAll, recording, elapsed, onToggleRecord,
  handsRaised, onGrant, onGrantAll, onOpenVote, onOpenYoutube, youtubeActive, onStopYoutube,
  presenterName, onStopPresenter,
}: Props) {
  const chip = 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors';
  return (
    <div className="flex items-center gap-2 flex-wrap px-3 py-2 border-b border-white/10 bg-white/[0.03]">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-accent-400 flex items-center gap-1">
        <i className="ri-vip-crown-2-line"></i>Hôte
      </span>

      <button
        onClick={onToggleMuteAll}
        className={`${chip} ${mutedAll ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white/10 text-white hover:bg-white/20'}`}
        title={mutedAll ? 'Rétablir les micros' : 'Couper le micro de tout le monde'}
      >
        <i className={mutedAll ? 'ri-mic-off-line' : 'ri-mic-line'}></i>
        {mutedAll ? 'Micros coupés' : 'Couper tout le monde'}
      </button>

      <button
        onClick={onToggleRecord}
        className={`${chip} ${recording ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white/10 text-white hover:bg-white/20'}`}
        title={recording ? "Arrêter l'enregistrement" : 'Enregistrer la réunion (vidéo + audio)'}
      >
        <i className={recording ? 'ri-stop-circle-line' : 'ri-record-circle-line'}></i>
        {recording ? `Arrêter · ${fmt(elapsed)}` : 'Enregistrer'}
      </button>

      {handsRaised.length > 0 && (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-400/15">
          <i className="ri-arrow-up-circle-line text-amber-400 text-xs"></i>
          <span className="text-[11px] text-amber-200">{handsRaised.length} main(s)</span>
          {handsRaised.slice(0, 2).map((h) => (
            <button
              key={h.id}
              onClick={() => onGrant(h.id)}
              className="px-2 py-0.5 rounded-full bg-amber-400 text-foreground-950 text-[10px] font-semibold cursor-pointer hover:bg-amber-300 whitespace-nowrap"
              title={`Autoriser ${h.name} à présenter`}
            >
              {h.name.split(' ')[0]}
            </button>
          ))}
          <button onClick={onGrantAll} className="px-2 py-0.5 rounded-full bg-white/15 text-white text-[10px] font-medium cursor-pointer hover:bg-white/25 whitespace-nowrap">
            Tous
          </button>
        </div>
      )}

      <button onClick={onOpenVote} className={`${chip} bg-white/10 text-white hover:bg-white/20`} title="Lancer un sondage">
        <i className="ri-bar-chart-2-line"></i>Sondage
      </button>

      {presenterName ? (
        <button onClick={onStopPresenter} className={`${chip} bg-accent-500 text-background-50 hover:bg-accent-600`} title="Retirer le rôle de présentateur">
          <i className="ri-presentation-line"></i>
          <span className="truncate max-w-[120px]">Présente : {presenterName}</span>
        </button>
      ) : (
        <span className={`${chip} bg-white/5 text-white/50`} title="Désignez un présentateur depuis la liste des participants">
          <i className="ri-presentation-line"></i>Présentateur : —
        </span>
      )}

      {youtubeActive ? (
        <button onClick={onStopYoutube} className={`${chip} bg-red-500/90 text-white hover:bg-red-600`} title="Arrêter la vidéo partagée">
          <i className="ri-stop-circle-line"></i>Arrêter la vidéo
        </button>
      ) : (
        <button onClick={onOpenYoutube} className={`${chip} bg-white/10 text-white hover:bg-white/20`} title="Partager une vidéo YouTube">
          <i className="ri-youtube-line"></i>YouTube
        </button>
      )}
    </div>
  );
}